const { cELM, getAnswerVariableCssClass, getQuestionTestId, globalCollabProvidersState, createCircuitBreakMessage } = require("../../utils.js");
const Provider = require("../blocks/provider.js");
const Option = require("../blocks/option.js");
const Question = require("../blocks/question.js");
const { cloneDeep } = require('lodash');

class CollaboratingProviders {
  constructor(params) {
    this._params = params;
    this._data = params.data;
    this.copyParams = cloneDeep(params);
    const cssVariableKey = getAnswerVariableCssClass(this._data);
    this._element = cELM("div", `component-block ${cssVariableKey}`);
    this._calendar = null;

    this.createElement();
  }

  initializeGlobalState(providerId) {
    if (!Object.keys(globalCollabProvidersState).includes(providerId)) {
      globalCollabProvidersState[providerId] = { clicked: false, lastDateRendered: null };
    }
  }

  createElement() {
    const value = this._data;
    const testId = getQuestionTestId(value);
    testId && this._element.setAttribute('data-testid', testId);
    value.status && this._element.classList.add(value.status);
    value.format && this._element.classList.add(value.format);

    this._element.classList.remove("singleselect");
    this._element.classList.remove("singleselect-insurance");
    this._element.classList.add("singleselectflex");

    // -------- Primary Provider -----------
    if (this._data.format) {
      const clinicIconBlock = new Provider(this._params);
      this._element.addELM(clinicIconBlock);
    }
    const selectApptTextBlock = new Question({ data: { text: 'Select an appointment date & time below.' } });
    this._element.addELM(selectApptTextBlock);
    // GO: Keep a reference so updateAnswer() can preserve this question text
    // (mirrors how non-collab questions show their text alongside the answer).
    this._questionTextElement = selectApptTextBlock.elm;

    // Why: when no provider was selected (skip-provider-selection flow uses the NPP sentinel as primary),
    // the primary card renders empty "No preference" placeholders. We still create the primary
    // Question + Option (this._calendar references it; updateAnswer/focus and the lock-state
    // rendering rely on the primary Option owning this._element) but skip adding the empty
    // placeholders to the DOM.
    const isNoPreferencePrimary = this._data.primaryProviderId === 'NPP';

    const primaryProviderBlock = new Question(this._params);
    if (!isNoPreferencePrimary) {
      this._element.addELM(primaryProviderBlock);
    }

    if (this._data.circuitBreakError) {
      this._element.addELM(createCircuitBreakMessage());
    }

    this._params.data.providerSlots = this._data.specificSlotsPerProvider[0]?.timeSlots;
    this._params.data.isCollabProvidersView = true;
    this._params.data.loadingState = false;
    this._params.data.currentProvider = this._data.primaryProviderId;

    this.initializeGlobalState(this._data.primaryProviderId);

    this._calendar = new Option(this._params);
    if (this._calendar.getHTML() && value.status !== "typing" && !isNoPreferencePrimary) {
      this._element.addELM(this._calendar);
    }
    // --------------------------------------

    // Collaborating Providers
    let collabProviderBlock;
    let calendarBlock;

    for (const provider of this._data.specificSlotsPerProvider) {
      if (provider.providerId === this._data.primaryProviderId) {
        continue;
      }

      // When the primary card is hidden (skip-provider-selection flow) the cards have only the name,
      // so center it vertically and skip the "Collaborating Provider" subtitle. Existing flows
      // (real primary + collab below) keep the original layout.
      const text = isNoPreferencePrimary
        ? `<div class='provider-info'><div class=img-wrapper><img src="${encodeURI(provider.providerImageUrl)}"></div><div style="align-self: center;">${provider.name}</div></div>`
        : `<div class='provider-info'><div class=img-wrapper><img src="${encodeURI(provider.providerImageUrl)}"></div><div>${provider.name}<div class='provider-info-location'><div>Collaborating Provider</div></div></div></div>`;
      collabProviderBlock = new Question({ data: { text }, style: 'margin-top: 20px' });
      this._element.addELM(collabProviderBlock);

      const newParams = cloneDeep(this.copyParams);
      newParams.data.providerSlots = provider.timeSlots;
      newParams.data.isCollabProvidersView = true;
      newParams.data.loadingState = false;
      newParams.data.currentProvider = provider.providerId;
      newParams.data.loadedMoreSlots = this._data.loadedMoreSlots;

      this.initializeGlobalState(provider.providerId);

      calendarBlock = new Option(newParams);
      if (calendarBlock.getHTML() && value.status !== "typing") {
        this._element.addELM(calendarBlock);
      }
    }
  }

  getELM() {
    return this._element;
  }

  getHTML() {
    return this._element;
  }

  updateAnswer(value, callback) {
    // GO: Hide calendars + provider blocks before showing the answer, but keep the
    // "Select an appointment date & time below." question text visible — same as how
    // every other answered question shows its question text on the left side of the chat.
    // Default option.updateAnswer only hides parentEl.lastChild, which leaves prior
    // calendars/providers visible in the collab providers layout. Also covers the
    // NPP-primary flow (skip-provider-selection): the universal hide collapses all
    // collab cards/calendars below, leaving only the question text + locked answer.
    for (const child of Array.from(this._element.children)) {
      if (child === this._questionTextElement) continue;
      child.style.display = 'none';
    }
    this._calendar.updateAnswer(value, this._element, callback);
  }

  focus() {
    this._calendar.focus();
  }
}

module.exports = CollaboratingProviders;
